/**
 * PublicContentRender
 *
 * Shared "happy path" file-viewer chrome for public content viewing.
 * Renders the UniversalFileViewer (or ArchivePreview for archives), content
 * metadata details, and download button.
 *
 * Used by:
 *   - PublicLinkAccess  (shared links via /p/:linkId)
 *   - PublicContentAccess (direct public content via /p/content/:contentId)
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import UniversalFileViewer from '../content/UniversalFileViewer';
import ArchivePreview from '../content/ArchivePreview';
import { ArchiveContentsSection, ArchiveManifest } from '../content/ArchiveContentsSection';
import { fetchArchiveEntryForPublicLink, ArchiveEntryError } from '../../services/archive.service';
import { StreamingMetadata, ViewerError } from '../../types/viewer.types';

export interface PublicLinkCredentials {
  linkId: string;
  token: string;
  password?: string;
  shareToken?: string;
  /** Short-lived JWT minted by accessPublicLink for archive entry fetches. */
  contentAccessToken?: string;
}

export interface PublicContentRenderProps {
  /** Content object from the public link / public content API response. */
  content: {
    id?: string;
    title: string;
    description?: string;
    mimeType?: string;
    mime_type?: string;
    fileSize?: number | string;
    file_size?: number | string;
    teamId?: string;
    team_id?: string;
  };
  /** Pre-built StreamingMetadata — computed by the parent (avoids duplicating logic here). */
  streamingMetadata: StreamingMetadata | undefined;
  /** Signed download URL (already fetched by the parent). */
  signedDownloadUrl: string | null;
  /** Credentials to pass through to UniversalFileViewer / archive entry fetches. */
  publicLinkCredentials?: PublicLinkCredentials;
  /** True while a URL is being fetched / a download is in flight. */
  fetchingUrl?: boolean;
  /** Called when the user presses the Download button. */
  onDownload: () => void;
  /** Called when the viewer emits an error. */
  onError?: (error: ViewerError) => void;
  /** When true, renders ArchivePreview + ArchiveContentsSection instead of the generic viewer. */
  isArchive?: boolean;
  /** Archive manifest from the content object. */
  archiveManifest?: ArchiveManifest | null;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const PublicContentRender: React.FC<PublicContentRenderProps> = ({
  content,
  streamingMetadata,
  signedDownloadUrl,
  publicLinkCredentials,
  fetchingUrl = false,
  onDownload,
  onError,
  isArchive = false,
  archiveManifest,
}) => {
  const mimeType = content.mimeType || content.mime_type || '';
  const rawFileSize = content.fileSize || content.file_size;
  const teamId = content.teamId || content.team_id;

  // Archive entry preview state (mirrors ContentDetailPage)
  const [selectedArchiveEntry, setSelectedArchiveEntry] = useState<{
    path: string;
    fileName: string;
    url: string;
    blob: Blob;
    mimeType: string;
  } | null>(null);
  const [archiveEntryLoading, setArchiveEntryLoading] = useState(false);
  const [archiveEntryError, setArchiveEntryError] = useState<{ code: string; message: string } | null>(null);

  // Revoke object URL on entry change / unmount.
  useEffect(() => {
    const url = selectedArchiveEntry?.url;
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [selectedArchiveEntry?.url]);

  const handleArchiveEntrySelect = useCallback(async (entryPath: string) => {
    if (!publicLinkCredentials) return;
    const { linkId, token, password, shareToken, contentAccessToken } = publicLinkCredentials;

    setSelectedArchiveEntry((prev) => {
      if (prev?.url) URL.revokeObjectURL(prev.url);
      return null;
    });
    setArchiveEntryLoading(true);
    setArchiveEntryError(null);

    try {
      const result = await fetchArchiveEntryForPublicLink(linkId, token, entryPath, password, shareToken, contentAccessToken);
      setSelectedArchiveEntry({
        path: entryPath,
        fileName: result.fileName,
        url: result.url,
        blob: result.blob,
        mimeType: result.mimeType,
      });
    } catch (err) {
      if (err instanceof ArchiveEntryError) {
        setArchiveEntryError({ code: err.code, message: err.message });
      } else {
        setArchiveEntryError({
          code: 'NETWORK_ERROR',
          message: err instanceof Error ? err.message : 'Failed to load entry',
        });
      }
    } finally {
      setArchiveEntryLoading(false);
    }
  }, [publicLinkCredentials]);

  const archiveFormat = (archiveManifest?.format ?? '').toLowerCase();
  const archiveInteractive = isArchive && !!publicLinkCredentials && ['zip', 'tar', 'tar.gz'].includes(archiveFormat);

  return (
    <>
      {/* Preview Section */}
      {isArchive ? (
        <Box sx={{ p: 3 }}>
          <ArchivePreview
            archiveManifest={archiveManifest}
            selectedEntry={selectedArchiveEntry}
            entryLoading={archiveEntryLoading}
            entryError={archiveEntryError}
            onDownloadArchive={onDownload}
          />
        </Box>
      ) : streamingMetadata && signedDownloadUrl ? (
        <Box sx={{ width: '100%', minHeight: 600 }}>
          <UniversalFileViewer
            contentId={content.id}
            teamId={teamId}
            initialMetadata={streamingMetadata}
            displayName={content.title}
            height={600}
            width="100%"
            showToolbar={true}
            showDownload={false}
            showFullscreen={true}
            publicLinkCredentials={publicLinkCredentials}
            onError={(error) => {
              console.error('Viewer error:', error);
              onError?.(error);
            }}
            onLoad={() => console.log('Viewer loaded')}
          />
        </Box>
      ) : (
        <Box
          sx={{
            height: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Loading viewer...
          </Typography>
        </Box>
      )}

      {/* Archive Contents Section */}
      {isArchive && (
        <Box sx={{ px: 3, pb: 3 }}>
          <ArchiveContentsSection
            manifest={archiveManifest}
            selectedEntryPath={selectedArchiveEntry?.path}
            onEntrySelect={archiveInteractive ? handleArchiveEntrySelect : undefined}
            interactive={archiveInteractive}
          />
        </Box>
      )}

      {/* Content Details Section */}
      <Box sx={{ p: 4 }}>
        {/* Title */}
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          {content.title}
        </Typography>

        {/* Description */}
        {content.description && (
          <Box
            sx={{ mt: 2, mb: 3, lineHeight: 1.7, color: 'text.secondary', typography: 'body1' }}
            dangerouslySetInnerHTML={{ __html: content.description }}
          />
        )}

        <Divider sx={{ my: 3 }} />

        {/* File Metadata */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            File Information
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {mimeType && (
              <Chip label={mimeType} size="small" variant="outlined" />
            )}
            {rawFileSize && (
              <Chip
                label={formatFileSize(parseInt(String(rawFileSize)))}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {/* Download Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          startIcon={<DownloadIcon />}
          onClick={onDownload}
          disabled={fetchingUrl}
          sx={{
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
          }}
        >
          {fetchingUrl ? 'Loading...' : 'Download'}
        </Button>

        {/* Footer Info */}
        <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" align="center" display="block">
            This content was shared with you via a secure link
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PublicContentRender;
